import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Bars4Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

import { memo, useMemo } from "react";

import clsx from "clsx";

import { apiBase } from "@/api/rest/core/apiClient";
import { Building } from "@/api/rest/models/building";
import { Household, HouseholdData } from "@/api/rest/models/household";
import DobuLogo from "@/assets/dobu-logo.svg";
import LennarLogo from "@/assets/partner-logos/lennar.svg";
import TerraLogo from "@/assets/partner-logos/terra.svg";

type Development = {
  id: number;
  name: string;
  buildings: (Building & { households: Household[] })[];
};

type NavbarLogoProps = {
  householdData?: HouseholdData;
  onToggleMenu: () => void;
  developments: Development[];
  selectedDevelopment?: Development;
  setSelectedDevelopment: (development: Development) => void;
  isMenuOpen?: boolean;
};

export const NavbarLogo = memo(function NavbarLogo({
  householdData,
  onToggleMenu,
  developments,
  selectedDevelopment,
  setSelectedDevelopment,
  isMenuOpen,
}: NavbarLogoProps) {
  const navigate = useNavigate();

  const sortedDevelopments = useMemo(() => {
    return [...developments].sort((a, b) => {
      const aIsDemo = a.buildings.some((building) => building.buildingId === 1);
      const bIsDemo = b.buildings.some((building) => building.buildingId === 1);

      if (aIsDemo && !bIsDemo) {
        return -1;
      }
      if (!aIsDemo && bIsDemo) {
        return 1;
      }

      return b.id - a.id;
    });
  }, [developments]);

  // Dynamic logo URL from the backend (preferred)
  const developmentLogoUrl = householdData?.building.developmentLogoUrl
    ? `${apiBase}${householdData.building.developmentLogoUrl}`
    : null;
  const logoMaxWidth = householdData?.building.developmentLogoMaxWidth ?? 140;
  const logoMaxHeight = householdData?.building.developmentLogoMaxHeight ?? 40;

  // Fallback to legacy partner code logos
  const showTerraLogo = !developmentLogoUrl && householdData?.building.partnerCode === "DEMO_EN";
  const showLennarLogo = !developmentLogoUrl && householdData?.building.partnerCode === "LENNAR";

  // Layout & Container Styles
  const containerClasses = clsx(
    // Basic layout
    "flex flex-col",
    // Spacing
    "mt-8 gap-2",
    // Alignment variations by screen size
    "items-stretch md:items-center",
    // Conditional margin at the bottom
    sortedDevelopments.length <= 1 ? "mb-4" : "mb-4 md:mb-0",
  );

  // Top Section Layout
  const topSectionClasses = clsx(
    // Basic layout
    "flex flex-row",
    // Alignment
    "justify-between items-center",
  );

  // Menu Icon Base Styles
  const menuIconBaseClasses = clsx(
    // Size and spacing
    "h-10 w-10 p-1",
    // Responsive visibility
    "inline md:hidden",
  );

  // Invisible placeholder for alignment
  const invisibleLeftIconClasses = clsx(menuIconBaseClasses, "ml-6", "invisible");

  // Right Menu Icon (Interactive)
  const rightMenuIconClasses = clsx(
    menuIconBaseClasses,
    // Positioning and sizing
    "mr-6 min-w-10 md:min-w-0",
    // Interaction
    "cursor-pointer",
    // Visual feedback
    "hover:bg-gray-100 hover:text-dobu-orange-focus rounded",
    // Additional responsive control
    "md:invisible",
  );

  // Logo Styles
  const dobuLogoClasses = clsx(
    // Visual
    "invert",
    // Spacing
    "mx-4",
    // Positioning
    "self-center",
    // Interaction
    "cursor-pointer",
  );

  const developmentDropdownContainerClasses = clsx("w-full px-4 py-2", isMenuOpen ? "block" : "hidden", "md:block");

  return (
    <div className={containerClasses}>
      {/* Top Section with Main Logo */}
      <div className={topSectionClasses}>
        <Bars4Icon className={invisibleLeftIconClasses} />
        <img
          className={dobuLogoClasses}
          src={DobuLogo}
          alt="Dobu Logo"
          width={100}
          height={24}
          onClick={() => navigate("/household")}
        />
        <Bars4Icon className={rightMenuIconClasses} onClick={onToggleMenu} />
      </div>
      {developmentLogoUrl && (
        <img
          className="mx-4 mt-4 self-center"
          src={developmentLogoUrl}
          alt="Development Logo"
          width={logoMaxWidth}
          height={logoMaxHeight}
          style={{ maxWidth: `${logoMaxWidth}px`, maxHeight: `${logoMaxHeight}px`, objectFit: "contain" }}
        />
      )}
      {showTerraLogo && (
        <img className="mx-4 mt-4 self-center" src={TerraLogo} alt="Terra Logo" width={100} height={24} />
      )}
      {showLennarLogo && <img className="mx-4 mt-4 self-center" src={LennarLogo} alt="Lennar Logo" width={140} />}
      {sortedDevelopments.length > 1 && (
        <div className={developmentDropdownContainerClasses}>
          <Menu as="div" className="relative inline-block text-left w-full">
            {({ open }) => (
              <>
                <div>
                  <MenuButton className="group inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-lg font-semibold text-gray-900 hover:bg-gray-50">
                    {selectedDevelopment?.name ?? "Select Development"}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className={clsx(
                        "-mr-1 h-5 w-5 text-gray-400 invisible group-hover:visible transition-transform",
                        open ? "rotate-0" : "-rotate-90",
                      )}
                    />
                  </MenuButton>
                </div>

                <MenuItems
                  transition
                  className="absolute left-0 z-10 mt-2 min-w-full w-max max-h-[70vh] origin-top-left overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    {sortedDevelopments.map((development) => (
                      <MenuItem key={development.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedDevelopment(development)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                        >
                          {development.name}
                        </button>
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </>
            )}
          </Menu>
        </div>
      )}
    </div>
  );
});
